import TextInput from '../TextInput';
import Combobox from '../Combobox.jsx';
import CustomTag from './CustomTag.jsx';
import TextArea from '../TextArea.jsx';
import EmailInput from '../EmailInput.jsx';
import SelectWithIcon from '../SelectWithIcon.jsx';
import { useState } from 'react';
import CustomFieldIcon from '../icons/CustomFieldIcon.jsx';
import Tooltip from '../Tooltip';

const DropdownField = ({ schema, value, onChange, className, hideTooltip }) => {
    const dropdownOptions = [
        { name: <span className="text-gray-500">None</span>, value: '' },
    ];

    const options = schema?.options || [];
    for (const opt of options) {
        dropdownOptions.push({
            name: (
                <div className="flex items-center space-x-4">
                    <CustomTag color={opt.color} value={opt.label} />
                </div>
            ),
            value: opt.label,
        });
    }

    // If a value is set but doesn't exist add it so it at least shows
    if (value && !options.includes(value)) {
        dropdownOptions.push({
            name: (
                <div className="flex items-center space-x-4">
                    <CustomTag value={value} />
                </div>
            ),
            value: value,
        });
    }

    return (
        <div className={className}>
            {!hideTooltip && (
                <Label label={schema.label} hideTooltip={hideTooltip} />
            )}
            <SelectWithIcon
                value={value || ''}
                onChange={onChange}
                options={dropdownOptions}
                btnClass="!bg-transparent focus:ring-none !ring-0 hover:!bg-gray-300 cursor-pointer"
                chevronClass="opacity-0 group-hover:opacity-100"
            />
        </div>
    );
};

const isYesValue = value =>
    typeof value === 'string' && value.trim().toLowerCase() === 'yes';

const Label = ({ label, hideTooltip }) => {
    return (
        <div className="flex items-center justify-between">
            <div className="input-label">{label}</div>
            {!hideTooltip && (
                <Tooltip message="Custom field, set up under Settings">
                    <CustomFieldIcon className="w-4 h-4 text-primary-700" />
                </Tooltip>
            )}
        </div>
    );
};

const Preview = ({ value, onEdit, className, schema, hideTooltip }) => {
    const type = schema.type;

    if (type === 'yesno') {
        if (isYesValue(value)) {
            value = <CustomTag color={schema?.color} value="Yes" />;
        } else {
            value = 'No';
        }
    }

    return (
        <div className={className} onClick={onEdit}>
            <Label label={schema.label} hideTooltip={hideTooltip} />
            <div
                className={`min-h-[42px] border border-transparent pl-3 flex items-center hover:bg-gray-300 rounded-lg cursor-pointer`}
            >
                {value || <span className="text-gray-500">None</span>}
            </div>
        </div>
    );
};

const SingleField = ({
    schema,
    value,
    onChange,
    className,
    alwaysEdit,
    hideTooltip,
}) => {
    alwaysEdit = !!alwaysEdit;
    const [isEditing, setIsEditing] = useState(alwaysEdit);

    if (schema.type === 'dropdown') {
        return (
            <DropdownField
                schema={schema}
                value={value}
                onChange={newValue => {
                    onChange(newValue);
                    setIsEditing(alwaysEdit);
                }}
                className={className}
                hideTooltip={hideTooltip}
            />
        );
    }

    if (!isEditing) {
        return (
            <Preview
                className={className}
                value={value}
                schema={schema}
                hideTooltip={hideTooltip}
                onEdit={() => setIsEditing(true)}
            />
        );
    }

    if (schema.type === 'paragraph') {
        return (
            <>
                <Label label={schema.label} hideTooltip={hideTooltip} />
                <TextArea
                    className={className}
                    value={value}
                    onChange={onChange}
                    autoFocus={true}
                    onBlur={() => setIsEditing(alwaysEdit)}
                />
            </>
        );
    }

    if (schema.type === 'email') {
        return (
            <div className={className}>
                <Label label={schema.label} hideTooltip={hideTooltip} />
                <EmailInput
                    value={value}
                    onChange={onChange}
                    autoFocus={true}
                    allowMultiple={true}
                    onBlur={() => setIsEditing(alwaysEdit)}
                />
            </div>
        );
    }

    if (schema.type === 'yesno') {
        return (
            <div className={className}>
                <Label label={schema.label} hideTooltip={hideTooltip} />
                <div>
                    <label>
                        <input
                            type="radio"
                            onChange={e => {
                                setIsEditing(alwaysEdit);
                                e.target.checked && onChange('yes');
                            }}
                            checked={isYesValue(value)}
                            className="mr-1"
                        />
                        Yes
                    </label>
                    <label className="ml-4">
                        <input
                            type="radio"
                            onChange={e => {
                                setIsEditing(alwaysEdit);
                                e.target.checked && onChange('');
                            }}
                            checked={!isYesValue(value)}
                            className="mr-1"
                        />
                        No
                    </label>
                </div>
            </div>
        );
    }

    return (
        <>
            <Label label={schema.label} hideTooltip={hideTooltip} />
            <TextInput
                value={value}
                onChange={onChange}
                className={className}
                autoFocus={true}
                onBlur={() => setIsEditing(alwaysEdit)}
            />
        </>
    );
};

export default SingleField;
